import React, { createContext, useContext, useState, useEffect } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await api.get("/auth/me");
      if (response.data.success) {
        let userData = response.data.data.user;

        // หากเป็น multi-company admin ให้โหลดข้อมูลบริษัทที่เลือกไว้จาก localStorage
        if (userData.role === "multi_company_admin") {
          const savedCompanyId = localStorage.getItem("current_company_id");
          const savedCompanyName = localStorage.getItem("current_company_name");
          const savedCompanyPermissions = localStorage.getItem(
            "company_permissions",
          );

          if (savedCompanyId && savedCompanyName) {
            userData = {
              ...userData,
              current_company_id: Number(savedCompanyId),
              current_company_name: savedCompanyName,
              company_permissions: savedCompanyPermissions
                ? JSON.parse(savedCompanyPermissions)
                : null,
            };

            // ตั้งค่า header สำหรับ API calls
            api.defaults.headers["x-company-id"] = savedCompanyId;
          }
        }

        setUser(userData);
      } else {
        localStorage.removeItem("auth_token");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("auth_token");
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post("/auth/login", { email, password });

      if (response.data.success) {
        const { user: userData, token } = response.data.data;

        localStorage.setItem("auth_token", token);
        setUser(userData);

        // Auto-select first company for multi-company admin
        if (userData.role === "multi_company_admin") {
          try {
            const companyResponse = await api.get(
              "/multi-company/accessible-companies",
            );
            if (
              companyResponse.data.success &&
              companyResponse.data.data.length > 0
            ) {
              const firstCompany = companyResponse.data.data[0];
              api.defaults.headers["x-company-id"] = firstCompany.company_id;

              const updatedUser = {
                ...userData,
                current_company_id: firstCompany.company_id,
                current_company_name: firstCompany.company_name,
                company_permissions: firstCompany,
              };
              updateUser(updatedUser);
            }
          } catch (error) {
            console.error("Failed to auto-select company:", error);
          }
        }

        message.success("เข้าสู่ระบบสำเร็จ!");
        navigate(
          userData.role === "super_admin"
            ? "/companies"
            : userData.role === "multi_company_patrol"
              ? "/guard-management"
              : "/patrol-reports",
        );
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "เข้าสู่ระบบล้มเหลว";
      return { success: false, message: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("selected_company_id");
      localStorage.removeItem("current_company_id");
      localStorage.removeItem("current_company_name");
      localStorage.removeItem("company_permissions");
      setUser(null);
      navigate("/");
      message.success("ออกจากระบบสำเร็จ");
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);

    // บันทึก current_company_id ลง localStorage เพื่อให้คงอยู่หลังรีเฟรช
    if (updatedUser?.current_company_id) {
      localStorage.setItem(
        "current_company_id",
        updatedUser.current_company_id,
      );
      localStorage.setItem(
        "current_company_name",
        updatedUser.current_company_name,
      );
      localStorage.setItem(
        "company_permissions",
        JSON.stringify(updatedUser.company_permissions),
      );
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
